import React, { useState, useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import "../styles/components/jobrunner.css";

const JobRunner = () => {
  const [arg1, setArg1] = useState("");
  const [arg2, setArg2] = useState("");
  const [arg3, setArg3] = useState("packone");
  const [outputMessage, setOutputMessage] = useState("");
  const [outputType, setOutputType] = useState(null); // 'loading' | 'success' | 'error'
  const [arg1Error, setArg1Error] = useState(false);
  const [arg2Error, setArg2Error] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();
  const [userEmail, setUserEmail] = useState(null);
  const API_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently().then((token) => {
        setAccessToken(token);
      });

      if (user && user.email) {
        setUserEmail(user.email);
      }
    }
  }, [isAuthenticated, getAccessTokenSilently, user]);

  const finishWithSuccess = (message) => {
    setOutputMessage(message || "Server opprettet!");
    setOutputType("success");
    setArg1("");
    setArg2("");
    setArg1Error(false);
    setArg2Error(false);
    isSubmittingRef.current = false;
    setIsSubmitting(false);
  };

  const finishWithError = (message) => {
    const lower = (message || "").toLowerCase();
    if (lower.includes("allerede i bruk") || lower.includes("already in use")) {
      setArg1Error(true);
    } else if (
      lower.includes("container") ||
      lower.includes("config") ||
      lower.includes("gtm")
    ) {
      setArg2Error(true);
    }
    setOutputMessage(message || "Noe gikk galt.");
    setOutputType("error");
    isSubmittingRef.current = false;
    setIsSubmitting(false);
  };

  const runScript = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setArg1Error(false);
    setArg2Error(false);
    setOutputType("loading");
    setOutputMessage("Starter jobb...");

    if (!isAuthenticated) {
      finishWithError("Du må være logget inn!");
      return;
    }

    if (!accessToken) {
      finishWithError("Autentisering feilet!");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/run-script`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "X-User-Email": userEmail,
        },
        body: JSON.stringify({ arg1, arg2, arg3 }),
      });

      if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || "Failed to submit job");
      }

      const { jobId } = await response.json();
      setOutputMessage("Jobb startet, sjekker status...");
      pollJobStatus(jobId);
    } catch (error) {
      finishWithError(error.message);
      console.error("Error:", error);
    }
  };

  const pollJobStatus = async (jobId) => {
    try {
      const response = await fetch(`${API_URL}/job-status/${jobId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to check job status");
      }

      const { status, result, error } = await response.json();

      if (status === "completed") {
        finishWithSuccess(result?.output);
      } else if (status === "failed") {
        finishWithError(error);
      } else {
        setOutputMessage("Oppretter server...");
        setTimeout(() => pollJobStatus(jobId), 2000);
      }
    } catch (error) {
      finishWithError(error.message);
      console.error("Error:", error);
    }
  };

  return (
    <div className="job-runner">
      <label htmlFor="arg1">Skriv inn domenet:</label>
      <input
        id="arg1"
        type="text"
        value={arg1}
        onChange={(e) => {
          setArg1(e.target.value);
          setArg1Error(false);
        }}
        placeholder="sst.domene.no"
        className={arg1Error ? "input-error" : ""}
      />
      <label htmlFor="arg2">
        Skriv inn Google Tag Manager Container config:
      </label>
      <input
        id="arg2"
        type="text"
        value={arg2}
        onChange={(e) => {
          setArg2(e.target.value);
          setArg2Error(false);
        }}
        placeholder="Container Config"
        className={arg2Error ? "input-error" : ""}
      />
      <label htmlFor="arg3">Velg grense for forespørsler:</label>
      <select id="arg3" value={arg3} onChange={(e) => setArg3(e.target.value)}>
        <option value="packone">10 000</option>
        <option value="packtwo">500 000</option>
        <option value="packthree">1 000 000</option>
        <option value="packfour">5 000 000</option>
        <option value="packfive">10 000 000</option>
      </select>

      <button
        onClick={runScript}
        disabled={!isAuthenticated || !accessToken || isSubmitting}
      >
        {isSubmitting ? "Oppretter..." : "Opprett"}
      </button>

      {outputType && (
        <div className={`job-output job-output--${outputType}`}>
          {outputType === "loading" && <span className="job-output__spinner" />}
          {outputMessage}
        </div>
      )}
    </div>
  );
};

export default JobRunner;
